import { useEffect, useMemo, useState } from "react";
import {
  useClient,
  useDraftTransactionOrders,
  useFundBasketLinkedSipChanges,
  useRebalanceFundBasketLinkedSips,
} from "creso-core/hooks";
import { convertToCurrency } from "creso-core/utils";
import { toast } from "sonner";
import { AlertTriangleIcon, HourglassIcon } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { AmcLogo } from "web-core";
import { handleError } from "@/utils/error";
import { FundBasketLinkedSipChange } from "creso-core/types";

const ACTION_BADGE: Record<
  string,
  { label: string; className: string } | undefined
> = {
  CANCEL: {
    label: "Not in Basket",
    className: "bg-tag-red text-tag-red-foreground",
  },
  CREATE: {
    label: "New in Basket",
    className: "bg-tag-green text-tag-green-foreground",
  },
};

function getActionAmounts(action: FundBasketLinkedSipChange) {
  return {
    current: action.current_amount ?? "0",
    withBasket: action.new_amount ?? "0",
  };
}

interface FundBasketLinkedSipChangesDialogProps {
  basketId: number;
  clientId: number | null;
  batchId: number | null;
  onOpenChange: (open: boolean) => void;
}

export function FundBasketLinkedSipChangesDialog({
  basketId,
  clientId,
  batchId,
  onOpenChange,
}: FundBasketLinkedSipChangesDialogProps) {
  const open = clientId != null;

  const { data: client, isLoading: isLoadingClient } = useClient(
    clientId as number
  );

  const { data: orders, isLoading: isLoadingOrders } =
    useDraftTransactionOrders(
      client?.investment_account || "",
      !!client?.investment_account
    );

  const hasDraftOrders = !!orders?.orders.results.length;

  const { data, isLoading: isLoadingChanges } = useFundBasketLinkedSipChanges(
    { basketId, clientId: clientId ?? 0 },
    { enabled: open }
  );

  const { mutateAsync: rebalance, isPending } =
    useRebalanceFundBasketLinkedSips(basketId);

  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setConfirming(false);
  }, [clientId]);

  const changeGroup = data?.find((change) => change.batch_id === batchId);

  const isBlocked = changeGroup?.changes?.some(
    (c) => c.status.value === "blocked"
  );

  const blockedReason = useMemo(() => {
    return changeGroup?.changes
      .filter((c) => c.status.value === "blocked")
      .map((c) => `${c.scheme_name}: ${c.status.details}`)
      .join("\n");
  }, [changeGroup]);

  const handleUpdate = async () => {
    if (!changeGroup) return;
    try {
      await rebalance({ batch_ids: [changeGroup.batch_id] });
      toast.success("Basket updated");
      setConfirming(false);
      onOpenChange(false);
    } catch (error) {
      handleError(error, "Failed to update basket");
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirming(false);
    onOpenChange(next);
  };

  const isLoading = isLoadingClient || isLoadingChanges || isLoadingOrders;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-240 min-h-100 p-0 max-w-4xl">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>
              {changeGroup?.client_name
                ? `${changeGroup.client_name}'s SIPs`
                : "SIPs"}
            </DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        <V2Loader isLoading={isLoading} className="px-5">
          <span className="text-lg py-5 font-semibold">
            {changeGroup?.client_name
              ? `${changeGroup.client_name}'s SIPs`
              : "SIPs"}
          </span>
          <div className="overflow-hidden rounded-lg mb-8 border border-border">
            <Table className="w-full text-sm">
              <TableHeader>
                <TableRow className="text-left bg-muted">
                  <TableHead className="py-3 font-semibold">Fund</TableHead>
                  <TableHead className="py-3 text-right font-semibold">
                    Current plan
                  </TableHead>
                  <TableHead className="py-3 text-right font-semibold">
                    Basket Profile
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changeGroup?.changes?.map((change) => {
                  const badge = ACTION_BADGE[change.action];
                  const { current, withBasket } = getActionAmounts(change);

                  return (
                    <TableRow
                      key={change.isin}
                      className="hover:bg-transparent"
                    >
                      <TableCell className="py-3">
                        <div className="flex flex-row items-center gap-3">
                          <AmcLogo
                            amc_code={change.amc_morningstar_code}
                            size="sm"
                          />
                          <span>{change.scheme_name}</span>
                          {badge && (
                            <Badge variant="custom" className={badge.className}>
                              {badge.label}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        {convertToCurrency(current)}
                      </TableCell>
                      <TableCell className="py-3 text-right font-semibold">
                        {convertToCurrency(withBasket)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {hasDraftOrders && (
            <div className="flex flex-col gap-1 rounded-lg border border-border bg-tag-yellow p-4">
              <div className="flex items-center gap-2">
                <HourglassIcon className="size-4 text-tag-yellow-foreground" />
                <span className="text-sm font-semibold text-tag-yellow-foreground">
                  There are pending orders in the cart
                </span>
              </div>
              <span className="pl-6 text-sm text-muted-foreground">
                Updating SIPs will replace them with new orders using the basket
                profile.
              </span>
            </div>
          )}
          {isBlocked && (
            <div className="flex flex-col gap-1 rounded-lg border border-border bg-tag-yellow p-4">
              <div className="flex items-center gap-2">
                <AlertTriangleIcon className="size-4 text-tag-yellow-foreground" />
                <span className="text-sm font-semibold text-tag-yellow-foreground">
                  Cannot Rebalance
                </span>
              </div>
              <span className="pl-6 text-sm text-muted-foreground">
                {blockedReason}
              </span>
            </div>
          )}
          <DialogFooter className="py-5 mt-6 border-t border-border">
            {confirming ? (
              <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
                <span className="text-sm">
                  <span className="font-semibold">Update SIPs?</span>{" "}
                  <span className="text-muted-foreground">
                    This will replace exiting orders in the cart.
                  </span>
                </span>
                <Button
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdate} disabled={isPending}>
                  Yes, update
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setConfirming(true)}
                disabled={!changeGroup || isBlocked}
                className="w-full sm:w-auto"
              >
                Update to Basket
              </Button>
            )}
          </DialogFooter>
        </V2Loader>
      </DialogContent>
    </Dialog>
  );
}
